/**
 * Niveaux de criticité (catégories) des commentaires de vouchers, avec leurs
 * phrases types cliquables. Source de vérité UI unique, consommée par la modale
 * de commentaires et par les badges d'état affichés dans l'onglet « Tickets »
 * (un badge par catégorie présente sur le ticket).
 *
 * La clé (`key`) est la valeur persistée côté back (choices sur ExamComment) ;
 * `null` = niveau par défaut, sans impact sur le passage de l'examen.
 * `severity` (0..3) est un ordinal purement front, découplé de la clé, pour
 * pouvoir réordonner/insérer des niveaux plus tard sans toucher aux clés en DB.
 *
 * Les trois classes couvrent les trois rendus d'un même niveau, qui partagent la
 * couleur Bootstrap : `badgeClass` pour le badge (commentaire et onglet Tickets),
 * `buttonClass` pour l'onglet sélectionné, `chipClass` pour ses phrases types.
 */
export interface CommentCategory {
    key: string | null;
    severity: number;
    label: string;
    badgeClass: string;
    buttonClass: string;
    chipClass: string;
    phrases: Array<string>;
}

export const EXAM_COMMENT_CATEGORIES: Array<CommentCategory> = [
    {
        key: null,
        severity: 0,
        label: "Observation",
        badgeClass: "badge-secondary",
        buttonClass: "btn-secondary",
        chipClass: "btn-outline-secondary",
        phrases: [
            "Problème technique ou matériel résolu sans conséquence",
            "Retard de soumission des fichiers, résolu avant la correction",
            "Déconnexion subite puis rétablissement de la connexion",
            "Remarque sur un comportement spécifique",
            "Observation générale sur le passage d'examen du candidat"
        ]
    },
    {
        key: "presence",
        severity: 1,
        label: "Absence/Retard",
        badgeClass: "badge-info",
        buttonClass: "btn-info",
        chipClass: "btn-outline-info",
        phrases: [
            "Candidat absent, sans justificatif",
            "Candidat en retard donc refusé à l'examen",
            "Candidat quittant la salle avant la fin sans rendre sa copie/son épreuve",
            "Candidat quittant temporairement la salle (pause sanitaire ou autre)",
            "Décision de report d'épreuve"
        ]
    },
    {
        key: "compliance",
        severity: 2,
        label: "Conformité",
        badgeClass: "badge-warning text-dark",
        buttonClass: "btn-warning",
        chipClass: "btn-outline-warning",
        phrases: [
            "Pièce d'identité manquante ou non conforme le jour J",
            "Participation du candidat non-confirmée",
            "Non-respect du formalisme de rendu (fichiers non fournis ou mal formattés…)",
            "Matériel inadapté",
            "Aménagement d'examen prévu mais partiellement appliqué"
        ]
    },
    {
        key: "critical",
        severity: 3,
        label: "Incident",
        badgeClass: "badge-danger",
        buttonClass: "btn-danger",
        chipClass: "btn-outline-danger",
        phrases: [
            "Exclusion pour fraude ou tentative de fraude",
            "Trouble du comportement nécessitant l'intervention du surveillant/jury",
            "Abandon volontaire en cours d'épreuve",
            "Interruption d'épreuve pour cas de force majeure individuel (malaise, évacuation)",
            "Litige ou contestation formelle du candidat pendant ou après l'épreuve (recours)",
            "Incident technique majeur invalidant une partie de l'épreuve",
            "Suspicion de fraude collective (plusieurs candidats, nécessitant enquête)",
            "Non-respect avéré d'un aménagement obligatoire ayant pénalisé le candidat"
        ]
    }
];

const DEFAULT_CATEGORY: CommentCategory = EXAM_COMMENT_CATEGORIES[0];

/** Retourne la catégorie correspondant à une clé (défaut « logistique » si null/inconnue). */
export function categoryByKey (key: string | null): CommentCategory {
    return EXAM_COMMENT_CATEGORIES.find((cat) => cat.key === key) ?? DEFAULT_CATEGORY;
}
